// The client for the ER module's TENANT-ADMIN surfaces (task 23's
// /api/cases/admin router).
//
// ======================================================================
// IT REUSES components/cases/api.js AND DOES NOT REBUILD IT
// ======================================================================
// That module already owns everything hard about talking to this API:
// the bearer token, the 401 -> SessionExpired conversion, the 403
// STEPUP_REQUIRED shape check (the SHAPE, never the status — a 403 that
// is not that one is a different refusal and must not raise a code
// prompt), the 503 ModuleUnavailable screen, and the rule that a 404 on
// a read means "not for you" and renders as nothing.
//
// Its `get`/`write` prefix every path with /api/cases, so the admin
// router's paths are simply '/admin/...' from here. A second client
// would be a second place for the token handling and the 403 rule to
// drift, on the one surface where getting the 403 rule wrong shows a
// code prompt that cannot fix anything.
//
// ======================================================================
// TWO SURFACES, TWO DIFFERENT GATES, AND THIS FILE KNOWS ABOUT NEITHER
// ======================================================================
// The org case list is behind step-up; case type administration is not.
// That is enforced by the BACKEND (er_cases/admin_routes.py attaches
// require_stepup to the case list route alone) and mirrored in the
// routing, not here. This file makes requests; the gates are in
// pages/admin/cases/*.
import { get, write } from '../../cases/api'

/** GET /api/cases/admin/cases -> OrgCaseListPage
 *
 *  BEHIND STEP-UP, AND IT WRITES AN AUDIT ROW ON THE SERVER — one per
 *  call, in audit_logs. That is why nothing in this application polls
 *  it, prefetches it, or calls it to render a count somewhere else: a
 *  read of the organisation's caseload is a recorded disclosure, and a
 *  recorded disclosure nobody actually looked at is noise in a trail
 *  that exists to be read.
 *
 *  THERE IS NO SEARCH PARAMETER AND THERE MUST NOT BE. The backend has
 *  none, deliberately: searching a list you cannot open is only useful
 *  for finding out whether a named person has a case. */
export function orgCases({ caseType, status, breached, sort, limit, offset } = {}) {
  const q = new URLSearchParams()
  if (caseType) q.set('case_type', caseType)
  if (status) q.set('status', status)
  if (breached !== null && breached !== undefined) q.set('breached', String(breached))
  if (sort) q.set('sort', sort)
  if (limit != null) q.set('limit', String(limit))
  if (offset != null) q.set('offset', String(offset))
  const qs = q.toString()
  return get(`/admin/cases${qs ? `?${qs}` : ''}`)
}

/** GET /api/cases/admin/case-types -> CaseTypeOut[]
 *
 *  NOT behind step-up. Configuration, naming nobody. */
export function caseTypes() {
  return get('/admin/case-types')
}

/** POST /api/cases/admin/case-types -> CaseTypeOut (201) */
export function createCaseType(body) {
  return write('/admin/case-types', { method: 'POST', body })
}

/** POST /api/cases/admin/case-types/{key}/preview -> CaseTypeMigrationOut
 *
 *  WRITES NOTHING. A POST because it carries a body, not because it
 *  changes anything — no version, no repoint, no audit row.
 *
 *  ONLY `stages` IS SENT, because the stage array is the only field
 *  that can strand a case: every other field applies to each case that
 *  moves and prevents none from moving. Sending more would imply the
 *  counts depend on it. */
export function previewCaseTypeUpdate(key, stages) {
  return write(`/admin/case-types/${encodeURIComponent(key)}/preview`, {
    method: 'POST',
    body: { stages },
  })
}

/** PATCH /api/cases/admin/case-types/{key} -> CaseTypeUpdateResult
 *
 *  `apply_to_open_cases` IS REQUIRED AND HAS NO DEFAULT, on the server
 *  and here. Defaulting it either way decides on the administrator's
 *  behalf what happens to cases already running. Every caller passes it
 *  explicitly, from an answer the user gave. */
export function updateCaseType(key, body) {
  return write(`/admin/case-types/${encodeURIComponent(key)}`, {
    method: 'PATCH',
    body,
  })
}

/** GET /api/cases/admin/case-roles -> CaseRoleDescription[]
 *
 *  The nine case roles, this organisation's names for them, and what
 *  each one can and cannot do. NOT behind step-up: a role label is
 *  org-wide configuration naming no employee and no case.
 *
 *  THE CAPABILITY SET COMES WITH THE LABEL, generated from
 *  acl.CAPABILITIES — the rename screen shows it read-only beside the
 *  editable name, so nobody renames a role while believing they are
 *  changing what it does. */
export function caseRoles() {
  return get('/admin/case-roles')
}

/** POST /api/cases/admin/case-roles -> CaseRoleOut (201)
 *
 *  Builds a role from Kwery's capability vocabulary. There is no `key`
 *  field: it is derived from the name once, server-side, and is never
 *  editable — a party row stores it, so changing it would orphan every
 *  live grant. */
export function createCaseRole(body) {
  return write('/admin/case-roles', { method: 'POST', body })
}

/** POST /api/cases/admin/case-roles/{key}/preview -> CaseRoleMigrationOut
 *
 *  WRITES NOTHING. What the same edit would do to the grants already
 *  made: how many take the new powers, how many cannot because doing so
 *  would put their holder on both sides of a separation on that case,
 *  and which those are. Shares _plan_role_migration with the real
 *  update, so the counts cannot disagree with what then happens. */
export function previewCaseRole(roleKey, capabilities) {
  return write(`/admin/case-roles/${encodeURIComponent(roleKey)}/preview`, {
    method: 'POST',
    body: { capabilities },
  })
}

/** PATCH /api/cases/admin/case-roles/{key} -> CaseRoleUpdateResult
 *
 *  Renames a role, redefines its powers, or retires it.
 *
 *  THE KEY IS A PATH SEGMENT AND IS NEVER IN THE BODY, so a request
 *  naming one role in the URL and another in the payload is
 *  unrepresentable. Nothing on this path can change a key: a party row
 *  stores it.
 *
 *  `apply_to_open_cases` IS REQUIRED. It decides whether people who
 *  already hold the role get the new powers, and there is no safe
 *  default. */
export function updateCaseRole(roleKey, body) {
  return write(`/admin/case-roles/${encodeURIComponent(roleKey)}`, {
    method: 'PATCH',
    body,
  })
}
